import { LinearProgress, Link } from "@mui/material";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import DetailsPanel from "components/Elements/DetailsPanel";
import {
	isoDateWithoutTimeZone,
	debounce,
	MuiFormatDate,
	getLocalStorageData,
	defaultPageSize,
	checkIsDefectPending,
	createTableSortHandler,
} from "helpers/utils";
import { useDispatch } from "react-redux";
import { showError } from "../../../redux/common/actions";
import Header from "./Header";
import { makeStyles } from "tss-react/mui";
import HasImages from "assets/icons/images.svg";
import SafteryCritical from "assets/icons/safety-critical.svg";
import DefectListTable from "./DefectsListTable";
import { appPath, defectsPath } from "helpers/routePaths";
import {
	buildDefectListUrl,
	saveDefectListNavIds,
} from "helpers/defectNavigation";

import ColourConstants from "helpers/colourConstants";
import CustomDateRange from "./CustomDateRange";
import DeleteDialog from "components/Elements/DeleteDialog";
import {
	getCountOfDefectsList,
	getDefectAutocompleteSearch,
	getDefectsList,
	getDefectSearchIds,
} from "services/defects/defectsList";
import { DefectTableColumns, DefectTableHeader } from "constants/DefectDetails";
import TabTitle from "components/Elements/TabTitle";
import mainAccess from "helpers/access";
import AutoCompleteBox from "components/Elements/EMIChip/AutoCompleteBox";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import ErrorMessageWithErrorIcon from "components/Elements/ErrorMessageWithErrorIcon";
import WarningMessageWithErrorIcon from "components/Elements/WarningMessageWithErrorIcon";
import { getIntegrationInstances } from "services/integrations";
import useChips from "hooks/useChips";
import { ChipTypeEnums } from "constants/enums";
import { FilterProvider, FilterContainer, useFilters } from "components/Elements/Filters";
import { useFilterInitialization } from "components/Elements/Filters/Dropdown/hooks/useFilterInitialization";
import { useDefectStatus } from "components/Elements/Filters/Dropdown/hooks/useDefectStatus";
import { getValidDepartmentIds } from "helpers/departments";
import {
	makeGetNextPageParam,
	removeRowFromInfiniteCache,
	flattenPages,
} from "helpers/infiniteQuery";

const useStyles = makeStyles()((theme) => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
		right: 0,
	},
	indicator: {
		display: "flex",
		gap: "6px",
		justifyContent: "center",
		alignItems: "center",
	}
}));

const defaultCustomDate = { from: "", to: "" };

// Filter configuration for the new Filter framework
const createFilterConfig = (
	hasIntegrationInstances = false,
	showFunctionalLocationOnly = false,
	showNewDefectsOnly = true
) => {
	const enableQuickFilter =
		hasIntegrationInstances || showFunctionalLocationOnly || showNewDefectsOnly;

	return {
		filters: {
			defectStatus: true,
			timeframe: {
			allowCustomRange: true,
			allowedRanges: ['today', 'yesterday', 'this-week', 'last-week', 'last-month', 'last-year', 'last-7-days', 'last-14-days', 'last-30-days', 'last-90-days', 'custom']
		},
			department: { multiSelect: true },
			quickFilter: {
				enabled: enableQuickFilter,
				multiSelect: true,
				persistAll: true,
				hasIntegrationInstances,
				showFunctionalLocationOnly,
				showNewDefectsOnly,
			},
			tableSort: true,
			chips: {
				enabled: true,
				hiddenTypes: [
					ChipTypeEnums.SiteDepartmentID,
					ChipTypeEnums.DefectStatusID,
					ChipTypeEnums.DefectStatusType,
				], // Hide department and defect status chips
			},
		},
	};
};

function DefectsListsContent({
	statusFromMemory,
	departmentFromMemory,
	timeFrameFromMemory,
	defectIntegrationInstance,
}) {
	const { filterValues, allFiltersInitialized, updateFilter, initialValues } = useFilters();

	// Get all defect statuses to expand group statuses into individual status IDs
	const allDefectStatuses = useDefectStatus(false);

	// Mark temp_asset_drilldown as used on mount (cleanup handled by FilterContext computeInitialValues)
	useEffect(() => {
		if (!localStorage || typeof localStorage.getItem !== 'function') {
			return;
		}
		const tempData = localStorage.getItem('temp_asset_drilldown');
		if (tempData) {
			try {
				const parsedData = JSON.parse(tempData);
				parsedData.isUsed = true;
				if (typeof localStorage.setItem === 'function') {
					localStorage.setItem('temp_asset_drilldown', JSON.stringify(parsedData));
				}
			} catch (error) {
				if (typeof localStorage.removeItem === 'function') {
					localStorage.removeItem('temp_asset_drilldown');
				}
			}
		}
	}, []);

	// Initialize chips using the generic useFilterInitialization hook
	useFilterInitialization('chips', [], {
		getDefaultValue: () => [],
		getParams: (chips) => ({ chips }),
		waitForData: false,
	});

	const dispatch = useDispatch();
	const { classes } = useStyles();
	const queryClient = useQueryClient();
	const chips = filterValues.chips?.value || [];

	// Memoized calculation of updated chips: preserve user chips + add chips
	// synthesised from department/status filter selections.
	const updatedChips = useMemo(() => {
		const currentChips = filterValues.chips?.value || [];
		const hiddenTypes = [ChipTypeEnums.SiteDepartmentID, ChipTypeEnums.DefectStatusID, ChipTypeEnums.DefectStatusType];

		const preservedChips = currentChips.filter(chip => !hiddenTypes.includes(chip.chipType));

		const departmentValue = filterValues.department?.value;
		const validDeptIds = getValidDepartmentIds(departmentValue);
		const departmentChips = validDeptIds.map(id => ({
			chipType: ChipTypeEnums.SiteDepartmentID,
			id,
			value: "",
		}));

		const statusParams = filterValues.defectStatus?.params;
		const statusChips = [];
		if (statusParams?.defectStatusType && statusParams.defectStatusType !== "") {
			const statusesOfType = allDefectStatuses.filter(
				status => status.type === statusParams.defectStatusType &&
				status.id !== "C" &&
				status.id !== "O" &&
				status.id !== ""
			);
			statusesOfType.forEach(status => {
				statusChips.push({
					chipType: ChipTypeEnums.DefectStatusID,
					id: status.id,
					value: "",
				});
			});
		} else if (statusParams?.defectStatusID && statusParams.defectStatusID !== "") {
			statusChips.push({
				chipType: ChipTypeEnums.DefectStatusID,
				id: statusParams.defectStatusID,
				value: "",
			});
		}

		return [...preservedChips, ...departmentChips, ...statusChips];
	}, [filterValues.chips?.value, filterValues.department?.value, filterValues.defectStatus?.params, allDefectStatuses]);

	const {
		isDropDownChanging,
		isRemovingChip,
		setDropDownChange,
		dataForFetchingDefect,
		setDataForFetchingDefect,
		autocompleteData,
		setAutocompleteData,
	} = useChips();

	const tableRef = useRef();

	// Handle autocomplete selection - integrate with Filter Framework
	const handleAutoCompleteContent = async (item) => {
		setDropDownChange(false);

		const currentChips = filterValues.chips?.value || [];
		const isDuplicate = currentChips.some(
			(existingItem) =>
				existingItem.id === item.id &&
				existingItem.chipType === item.chipType &&
				existingItem.value === item.value
		);

		if (!isDuplicate) {
			const updatedChipsLocal = [...currentChips, item];
			updateFilter('chips', updatedChipsLocal, { chips: updatedChipsLocal });
		}

		setAutocompleteData([]);
		setDataForFetchingDefect({
			pageNumber: 1,
			pageSize: defaultPageSize(),
			search: "",
			sortField: "",
			sort: "",
		});
	};

	const {
		customCaptions,
		position,
		siteAppID,
		siteID,
		application,
		site: { siteDepartmentID, siteDepartmentName },
		weekStartDay,
	} = getLocalStorageData("me");

	const tableSortParams = filterValues.tableSort?.params || { sortField: "", sortOrder: "asc" };
	const currentTableSort = [tableSortParams.sortField, tableSortParams.sortOrder];

	const [isSearching, setSearching] = useState(false);
	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [customDate, setCustomDate] = useState(
		timeFrameFromMemory !== null && timeFrameFromMemory.id === 7
			? {
					from: MuiFormatDate(timeFrameFromMemory.fromDate),
					to: MuiFormatDate(timeFrameFromMemory.toDate),
			  }
			: defaultCustomDate
	);
	const [openCustomDatePopup, setOpenCustomDatePopup] = useState(false);
	const [isCustomDateRangeError, setCustomDateRangeError] = useState(false);
	const searchRef = useRef("");

	// Frozen at mount so a viewport resize can't desync paging.
	const [pageSize] = useState(() => defaultPageSize());

	const statusParams = filterValues.defectStatus?.params;
	const timeframeValue = filterValues['timeframe']?.value;
	const quickFilterParams = filterValues['quickFilter']?.params;
	const currentFilters = useMemo(() => ({
		defectStatusID: statusParams?.defectStatusID || '',
		defectStatusType: statusParams?.defectStatusType || '',
		fromDate: timeframeValue?.fromDate || '',
		toDate: timeframeValue?.toDate || '',
		sortField: tableSortParams.sortField || '',
		sortOrder: tableSortParams.sortOrder || 'asc',
		showSAPErrorsOnly: quickFilterParams?.showSAPErrorsOnly || false,
		showFunctionalLocationOnly: quickFilterParams?.showFunctionalLocationOnly || false,
		showNewDefectsOnly: quickFilterParams?.showNewDefectsOnly || false,
	}), [
		statusParams?.defectStatusID,
		statusParams?.defectStatusType,
		timeframeValue?.fromDate,
		timeframeValue?.toDate,
		tableSortParams.sortField,
		tableSortParams.sortOrder,
		quickFilterParams?.showSAPErrorsOnly,
		quickFilterParams?.showFunctionalLocationOnly,
		quickFilterParams?.showNewDefectsOnly,
	]);

	const defectsQueryKey = useMemo(
		() => [
			'defects',
			'list',
			{
				siteAppId: siteAppID,
				chips: updatedChips,
				...currentFilters,
				pageSize,
			},
		],
		[siteAppID, updatedChips, currentFilters, pageSize]
	);

	const defectsQuery = useInfiniteQuery({
		queryKey: defectsQueryKey,
		enabled: allFiltersInitialized && Boolean(siteAppID),
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const response = await getDefectsList({
				siteAppId: siteAppID,
				chips: updatedChips,
				fromDate: currentFilters.fromDate,
				toDate: currentFilters.toDate,
				sortField: currentFilters.sortField,
				sortOrder: currentFilters.sortOrder,
				pageNumber: pageParam,
				pageSize,
				search: searchRef.current,
				onlySAPErrors: currentFilters.showSAPErrorsOnly,
				onlyFLOC: currentFilters.showFunctionalLocationOnly,
				onlyNewDefects: currentFilters.showNewDefectsOnly,
			});
			if (!response?.status) {
				throw new Error(response?.data?.detail || `Failed to fetch ${customCaptions?.defect} list`);
			}
			return { rows: Array.isArray(response.data) ? response.data : [], pageParam };
		},
		getNextPageParam: makeGetNextPageParam(pageSize),
	});

	useEffect(() => {
		if (defectsQuery.isError) {
			dispatch(showError(defectsQuery.error?.message || `Failed to fetch ${customCaptions?.defect} list`));
		}
	}, [defectsQuery.isError, defectsQuery.error, dispatch, customCaptions?.defect]);

	// Search is intentionally omitted from the key: chip selection is what drives
	// the filter set; the queryFn reads searchRef at fetch time.
	const countQueryKey = useMemo(
		() => [
			'defects',
			'count',
			{
				siteAppId: siteAppID,
				chips: updatedChips,
				fromDate: currentFilters.fromDate,
				toDate: currentFilters.toDate,
				onlySAPErrors: currentFilters.showSAPErrorsOnly,
				onlyFLOC: currentFilters.showFunctionalLocationOnly,
				onlyNewDefects: currentFilters.showNewDefectsOnly,
			},
		],
		[siteAppID, updatedChips, currentFilters]
	);

	const countQuery = useQuery({
		queryKey: countQueryKey,
		enabled: allFiltersInitialized && Boolean(siteAppID),
		queryFn: async () => {
			const response = await getCountOfDefectsList({
				siteAppId: siteAppID,
				chips: updatedChips,
				search: searchRef.current,
				fromDate: currentFilters.fromDate,
				toDate: currentFilters.toDate,
				onlySAPErrors: currentFilters.showSAPErrorsOnly,
				onlyFLOC: currentFilters.showFunctionalLocationOnly,
				onlyNewDefects: currentFilters.showNewDefectsOnly,
			});
			return response?.status ? response.data : { totalCount: 0, unacknowledgedCount: 0 };
		},
	});

	const idsQuery = useQuery({
		queryKey: [
			"defects",
			"ids",
			{ siteAppId: siteAppID, chips: updatedChips, ...currentFilters },
		],
		enabled: allFiltersInitialized && Boolean(siteAppID),
		queryFn: async () => {
			const response = await getDefectSearchIds({
				siteAppId: siteAppID,
				chips: updatedChips,
				search: searchRef.current,
				fromDate: currentFilters.fromDate,
				toDate: currentFilters.toDate,
				sortField: currentFilters.sortField,
				sortOrder: currentFilters.sortOrder,
				onlySAPErrors: currentFilters.showSAPErrorsOnly,
				onlyFLOC: currentFilters.showFunctionalLocationOnly,
				onlyNewDefects: currentFilters.showNewDefectsOnly,
			});
			return response?.status ? response.data : [];
		},
	});

	useEffect(() => {
		saveDefectListNavIds(idsQuery.data);
	}, [idsQuery.data]);

	const allData = useMemo(() => flattenPages(defectsQuery.data), [defectsQuery.data]);

	const totalCount = countQuery.data?.totalCount ?? 0;
	const unacknowledgedCount = countQuery.data?.unacknowledgedCount ?? 0;

	const buildDefectUrl = useCallback(
		(id) => buildDefectListUrl(id, idsQuery.data),
		[idsQuery.data]
	);

	const formattedData = useCallback((data, navigate) => {
		return data.map((x) => ({
			...x,
			modelName: x.modelModel ? x.modelName + " " + x.modelModel : x.modelName,
			taskName: x?.actionName ? x?.actionName + " " + x?.taskName : x?.taskName,
			defectStatusName: (
				<span
					style={{
						color: x?.defectStatusType === "C" ? ColourConstants.green : "",
					}}
				>
					{x?.defectStatusName}
				</span>
			),
			riskRatingName: (
				<span
					style={{
						color: x?.safetyCritical === true ? ColourConstants.red : "",
					}}
				>
					{x?.riskRatingName}
				</span>
			),
			number: (
				<>
					<Link
						onClick={() => navigate(buildDefectUrl(x.id))}
						href={buildDefectUrl(x.id)}
						style={{
							color: ColourConstants.activeLink,
							cursor: "pointer",
							textDecoration: "none",
						}}
					>
						{x.number}
					</Link>
					&nbsp;
					{x?.sapError && <ErrorMessageWithErrorIcon message={x?.sapError} />}
					{!x?.exported &&
					checkIsDefectPending(x, defectIntegrationInstance) ? (
						<WarningMessageWithErrorIcon
							message={`Defect not synchronized to ${defectIntegrationInstance?.name}`}
						/>
					) : null}
				</>
			),
			safetyCritical: (
					<div
					className={classes.indicator}
				>
					{x.safetyCritical === true ? (
						<img src={SafteryCritical} alt="safety critical" />
					) : (
						""
					)}

					{x.hasImages && (
						<HtmlTooltip title="Has Images">
							<img src={HasImages} alt="" style={{ width: 20, height: 20 }} />
						</HtmlTooltip>
					)}
				</div>
			),

			createdDateTime: isoDateWithoutTimeZone(x.createdDateTime + "Z"),
			details: (
				<HtmlTooltip title={x.details}>
					<p className="max-two-line">
						<Link
							onClick={() => navigate(`${appPath}${defectsPath}/${x.id}`)}
							href={`${appPath}${defectsPath}/${x.id}`}
							style={{
								color: ColourConstants.commonText,
								cursor: "pointer",
								textDecoration: "none",
							}}
						>
							{x.details}
						</Link>
					</p>
				</HtmlTooltip>
			),
		}));
	}, [classes.indicator, defectIntegrationInstance, buildDefectUrl]);

	const handleCustomDateChange = (type, e) => {
		setCustomDate({ ...customDate, [type]: e.target.value });
	};

	const handleCloseCustomDate = () => {
		setOpenCustomDatePopup(false);
		setCustomDateRangeError(defaultCustomDate);
	};

	const handleCustomDateSubmit = async (e) => {
		e.preventDefault();
		const isFromDateEmpty = customDate.from === "";
		const isToDateEmpty = customDate.to === "";
		if (isFromDateEmpty || isToDateEmpty) {
			setCustomDateRangeError({ from: isFromDateEmpty, to: isToDateEmpty });
			return;
		}
		handleCloseCustomDate();
	};

	// searching for defects
	const handleSearch = debounce(async (value) => {
		setSearching(true);
		searchRef.current = value;
		const autoCompleteRes = await getDefectAutocompleteSearch({
			search: value,
		});

		setAutocompleteData(autoCompleteRes?.data);
		if (!value || value === "")
			setDataForFetchingDefect({
				pageNumber: 1,
				pageSize: defaultPageSize(),
				search: "",
				sortField: "",
				sort: "",
			});
		setSearching(false);
	}, 500);

	// opens DELETE defect popup
	const handleDeleteDialogOpen = (id) => {
		setDeleteID(id);
		setOpenDeleteDialog(true);
	};

	// fetching the chips
	useEffect(() => {
		if (dataForFetchingDefect?.search.length >= 3) {
			handleSearch(dataForFetchingDefect?.search);
		} else {
			setAutocompleteData([]);
		}
	}, [dataForFetchingDefect?.search]);

	const handleRemoveData = (id) => {
		removeRowFromInfiniteCache(queryClient, defectsQueryKey, (row) => row.id === id);
		queryClient.invalidateQueries({ queryKey: countQueryKey });
	};

	// Re-apply body overflow styles on site navigation — switching sites can
	// remount layout chrome that resets these. siteID is the trigger only.
	useEffect(() => {
		document.body.style.overflowX = "hidden";
		document.body.style.maxWidth = "100%";
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteID]);

	const handleTableSort = (sortField, sortOrder) => {
		createTableSortHandler(tableSortParams, updateFilter)(sortField, sortOrder);
	};

	const tableIsRefreshing =
		(defectsQuery.isFetching && !defectsQuery.isFetchingNextPage) ||
		countQuery.isFetching;

	return (
		<>
			<TabTitle
				title={`${customCaptions.defectPlural} | ${application.name}`}
			/>
			{(isSearching || isRemovingChip || tableIsRefreshing) && (
				<LinearProgress className={classes.loading} />
			)}

			<DeleteDialog
				entityName={customCaptions?.defect}
				open={openDeleteDialog}
				closeHandler={() => setOpenDeleteDialog(false)}
				deleteID={deleteID}
				deleteEndpoint={"/api/defects"}
				handleRemoveData={handleRemoveData}
			/>
			<CustomDateRange
				open={openCustomDatePopup}
				handleChange={handleCustomDateChange}
				customDate={customDate}
				closeHandler={handleCloseCustomDate}
				onSubmit={handleCustomDateSubmit}
				isError={isCustomDateRangeError}
				isLoading={isSearching}
			/>
			<div className="container">
				<Header
					dataLength={totalCount}
					defectsCC={customCaptions?.defectPlural}
					customCaptions={customCaptions}
				/>
				<div
					className="detailsContainer"
					style={{ alignItems: "center", marginTop: "-15px" }}
				>
					<DetailsPanel
						showHeader={false}
						description={`View all ${customCaptions?.defectPlural} across your operations`}
					/>
					<AutoCompleteBox
						searchQuery={dataForFetchingDefect?.search}
						setSearchQuery={(e) => {
							e.persist();
							setDataForFetchingDefect((prev) => ({
								...prev,
								search: e.target.value,
							}));
						}}
						width="100%"
						required
						label={`Search ${customCaptions?.defectPlural ?? "Defects"}`}
						setSelectedValue={handleAutoCompleteContent}
						selectdValueToshow="name"
						dataSource={autocompleteData}
						isSearching={isSearching}
						chips={chips}
					/>
				</div>

				{/* Filter Framework with ChipsFilter */}
				<div style={{ marginTop: 20 }}>
					<FilterContainer />
				</div>

				<div className="dynamic-width-table">
					<DefectListTable
						ref={tableRef}
						data={allData}
						headers={DefectTableHeader(customCaptions)}
						columns={DefectTableColumns()}
						handleSort={handleTableSort}
						formattedData={formattedData}
						handleDeleteDialogOpen={handleDeleteDialogOpen}
						currentTableSort={currentTableSort}
						isReadOnly={position?.[mainAccess.defectAccess] === "R"}
						hasNextPage={defectsQuery.hasNextPage}
						isFetchingNextPage={defectsQuery.isFetchingNextPage}
						fetchNextPage={defectsQuery.fetchNextPage}
						isFetchNextPageError={defectsQuery.isFetchNextPageError}
					/>
				</div>
			</div>
		</>
	);
}

// Wrapper component that provides FilterContext
function DefectsLists(props) {
	const [hasIntegrationInstances, setHasIntegrationInstances] = useState(false);
	const [defectIntegrationInstance, setDefectIntegrationInstance] = useState(null);
	const { application } = getLocalStorageData("me");
	const showAssetReferences = application?.showAssetReferences || false;

	useEffect(() => {
		const fetchIntegrationInstances = async () => {
			try {
				const response = await getIntegrationInstances();
				if (response?.status && response?.data?.length) {
					const sapHanaInstances = response.data.filter(
						instance => instance.integrationType === "SAP-Hana-Cloud" && instance.isActive
					);

					if (sapHanaInstances.length > 0) {
						const formattedIntegrationInstance = {
							...sapHanaInstances[0],
							fieldMappings: sapHanaInstances[0]?.fieldMappings
								? JSON.parse(sapHanaInstances[0].fieldMappings) ?? {}
								: [],
							settings: sapHanaInstances[0]?.settings
								? JSON.parse(sapHanaInstances[0].settings) ?? {}
								: [],
						};
						setDefectIntegrationInstance(formattedIntegrationInstance);
						setHasIntegrationInstances(true);
					} else {
						setHasIntegrationInstances(false);
					}
				} else {
					setHasIntegrationInstances(false);
				}
			} catch (error) {
				console.error("Error fetching integration instances:", error);
				setHasIntegrationInstances(false);
			}
		};

		fetchIntegrationInstances();
	}, []);

	const filterConfig = useMemo(
		() => createFilterConfig(hasIntegrationInstances, showAssetReferences),
		[hasIntegrationInstances, showAssetReferences]
	);

	return (
		<FilterProvider
			config={filterConfig}
			storageKey="defects"
		>
			<DefectsListsContent {...props} defectIntegrationInstance={defectIntegrationInstance} />
		</FilterProvider>
	);
}

export default DefectsLists;
